/**
 * Forum message HTML: legacy BB/HTML from the backend, same pipeline as ForumsPage.
 * Strips scripts / inline handlers, expands card notation and linkifies URLs.
 * Smileys texte (compat Phorum) → emoji Unicode hors balises HTML.
 */

import { convertEmoticonsToEmojisOutsideTags } from './emoticons';

const SUIT_MAP: Record<string, number> = { C: 0, P: 14, K: 28, T: 42, A: 56 };

function parseCardCode(code: string): number | null {
  if (code.length < 2) return null;
  const suitChar = code[0].toUpperCase();
  const valueStr = code.substring(1).toUpperCase();
  const offset = SUIT_MAP[suitChar];
  if (offset === undefined) return null;
  if (suitChar === 'A' && valueStr === 'E') return 78;
  const vmap: Record<string, number> = { V: 11, D: 13, R: 14 };
  if (suitChar !== 'A') vmap.C = 12;
  let value: number;
  if (vmap[valueStr] !== undefined) value = vmap[valueStr];
  else {
    value = parseInt(valueStr, 10);
    if (isNaN(value) || value < 1) return null;
  }
  if (suitChar === 'A') {
    if (value < 1 || value > 21) return null;
    return 56 + value;
  }
  if (value < 1 || value > 14) return null;
  return offset + value;
}

function renderCardHand(marker: string): string {
  const codes = marker.split('-').filter(Boolean);
  const cards = codes.map(parseCardCode).filter((n): n is number => n !== null);
  if (cards.length === 0) return marker;
  return (
    '<span class="forum-card-hand">' +
    cards
      .map(
        (n, i) =>
          `<img class="forum-card-img" src="/cards/${n}.png" alt="" draggable="false" style="margin-left:${i > 0 ? '-20px' : '0'};z-index:${i}">`,
      )
      .join('') +
    '</span>'
  );
}

/** Drop legacy color/background that clash with Neo light/dark themes. */
function stripThemeClashingStyles(html: string): string {
  return html
    .replace(/\sstyle\s*=\s*(["'])([\s\S]*?)\1/gi, (_m, quote: string, style: string) => {
      const kept = style
        .split(';')
        .map((part) => part.trim())
        .filter((part) => {
          if (!part) return false;
          const prop = part.split(':')[0].trim().toLowerCase();
          return !(
            prop === 'color'
            || prop === 'background'
            || prop === 'background-color'
            || prop === 'background-image'
            || prop.startsWith('background-')
          );
        });
      if (kept.length === 0) return '';
      return ` style=${quote}${kept.join('; ')}${quote}`;
    })
    // <font color="…"> / bgcolor="…"
    .replace(/\s(?:bg)?color\s*=\s*(["']?)[^"'>\s]*\1/gi, '');
}

export function processForumContent(html: string): string {
  if (!html) return '';
  let safe = html
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/on\w+\s*=/gi, 'data-blocked=');
  safe = stripThemeClashingStyles(safe);
  safe = safe.replace(/##([A-Za-z0-9\-]+)##/g, (_m, inner) => renderCardHand(inner));
  const suitLineRegex =
    /(?:^|\n)\s*([CPKTA])\s*:\s*((?:(?:As|[RVCD]|[0-9]{1,2}|x)\s*[-–]\s*)*(?:As|[RVCD]|[0-9]{1,2}|x))/gim;
  const handCards: number[] = [];
  let m: RegExpExecArray | null;
  const snapshot = safe;
  while ((m = suitLineRegex.exec(snapshot)) !== null) {
    const sc = m[1].toUpperCase();
    for (const v of m[2]
      .split(/\s*[-–]\s*/)
      .map((s) => s.trim())
      .filter(Boolean)) {
      const cn = parseCardCode(sc + (v.toLowerCase() === 'as' ? '1' : v));
      if (cn !== null) handCards.push(cn);
    }
  }
  if (handCards.length >= 6) {
    safe = safe.replace(suitLineRegex, '');
    const imgs = handCards
      .map(
        (n, i) =>
          `<img class="forum-card-img" src="/cards/${n}.png" alt="" draggable="false" style="margin-left:${i > 0 ? '-20px' : '0'};z-index:${i}">`,
      )
      .join('');
    safe = `<div class="forum-card-hand">${imgs}</div>` + safe;
  }
  safe = safe.replace(
    /(?<!="|'>)(https?:\/\/[^\s<"')\]]+)/gi,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
  );
  if (!safe.includes('<p>') && !safe.includes('<div') && !safe.includes('<br')) {
    safe = safe.replace(/\n/g, '<br>');
  }
  return convertEmoticonsToEmojisOutsideTags(safe);
}

export function stripHtml(html: string): string {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}
